// ── ProductDetailModal.jsx ────────────────────────────────────────────────────
// Main orchestrator — wires hooks to sub-components, holds no business logic
// ✅ FIXED: passes isFollowLoading to SellerSidebar
// ✅ FIXED: normalises product.images so video URLs are plain strings,
//           never passed as objects to ProductImagesSection

import React from 'react';
import styles from './ProductDetailModal.module.css';

// Hooks
import useProductModal from './hooks/useProductModal';

// Sub-components
import ProductImagesSection  from './components/ProductImagesSection';
import ProductInfoSection    from './components/ProductInfoSection';
import SellerSidebar         from './components/SellerSidebar';
import SimilarItems          from './components/SimilarItems';
import MakeOfferModal        from './components/MakeOfferModal';
import MessageSellerModal    from './components/MessageSellerModal';
import RateSellerModal       from './components/RateSellerModal';
import ReportSellerModal     from './components/ReportSellerModal';

// ─── Helper: extract a plain URL string from whatever shape the backend returns
// Handles:  "https://…/video.mp4"
//           { url: "…", type: "video" }
//           { image: "…" }
//           { file: "…" }
//           { src: "…" }
const SERVER_BASE = import.meta.env?.VITE_SERVER_BASE || 'https://imselling-marketplace-production.up.railway.app';

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) return url;
  if (url.startsWith('/')) return `${SERVER_BASE}${url}`;
  return `${SERVER_BASE}/${url}`;
};

const extractUrl = (item) => {
  if (!item) return null;
  const raw = typeof item === 'string'
    ? item
    :(item.url || item.image_url || item.image || item.file || item.src || item.media || null);              //(item.url || item.image || item.file || item.src || item.media || null);
  return resolveUrl(raw);
};

// ─── Helper: build a clean, deduplicated array of URL strings ─────────────────
const buildProductImages = (product) => {
  let raw = [];

  if (product.images && Array.isArray(product.images) && product.images.length > 0) {
    raw = product.images;
  } else if (product.img) {
    raw = [product.img];
  } else if (product.image) {
    raw = [product.image];
  }

  const urls = raw
    .map(extractUrl)
    .filter(Boolean);

  return urls.length > 0
    ? urls
    : ['https://via.placeholder.com/600x400?text=No+Image'];
};

// ─── ProductDetailModal ───────────────────────────────────────────────────────
const ProductDetailModal = ({
  isOpen,
  product,
  onClose,
  getRelatedProducts,
  onProductClick,
  isLoggedIn,
  onOpenLoginModal,
  isLoading = false,   // ← true while App is fetching the product from URL
}) => {
  // ── All state & handlers come from the hook ───────────────────────────────
  const modal = useProductModal({
    product,
    isLoggedIn,
    isOpen,
    onOpenLoginModal,
    onProductClick,
  });

  // ── Skeleton: modal is open but product hasn't loaded yet ─────────────────
  if (isOpen && (isLoading || !product)) {
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.productDetailModal}>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
          <div className={styles.skeletonContent}>

            {/* LEFT — image skeleton */}
            <div className={styles.skeletonLeft}>
              <div className={styles.skeletonMainImage} />
              <div className={styles.skeletonThumbRow}>
                {[0,1,2,3].map(i => <div key={i} className={styles.skeletonThumb} />)}
              </div>
            </div>

            {/* MIDDLE — info skeleton */}
            <div className={styles.skeletonMiddle}>
              <div className={styles.skeletonTitle} />
              <div className={styles.skeletonPrice} />
              <div className={styles.skeletonBadge} />
              <div className={styles.skeletonLine} />
              <div className={styles.skeletonLineShort} />
              <div className={styles.skeletonLine} />
              <div className={styles.skeletonLineShort} />
              <div style={{ marginTop: 8 }}>
                <div className={styles.skeletonBtn} style={{ marginBottom: 10 }} />
                <div className={styles.skeletonBtnLarge} style={{ marginBottom: 10 }} />
                <div className={styles.skeletonBtnLarge} />
              </div>
            </div>

            {/* RIGHT — seller skeleton */}
            <div className={styles.skeletonRight}>
              <div className={styles.skeletonAvatarRow}>
                <div className={styles.skeletonAvatar} />
                <div className={styles.skeletonAvatarInfo}>
                  <div className={styles.skeletonTitle} style={{ height: 18, width: '70%' }} />
                  <div className={styles.skeletonLine} style={{ width: '50%' }} />
                </div>
              </div>
              <div className={styles.skeletonBtn} />
              <div className={styles.skeletonLine} />
              <div className={styles.skeletonLineShort} />
              <div className={styles.skeletonLine} />
              <div className={styles.skeletonLineShort} />
              <div className={styles.skeletonLine} />
            </div>

          </div>
        </div>
      </div>
    );
  }

  // ── Early exit if neither open nor product ────────────────────────────────
  if (!isOpen || !product) return null;

  // ── Derived data ──────────────────────────────────────────────────────────
  const relatedProducts = getRelatedProducts(product);

  const sellerOtherProducts = relatedProducts
    .filter(p => p.seller === product.seller && p.id !== product.id)
    .slice(0, 4);

  // ✅ Always plain URL strings — never objects
  const productImages = buildProductImages(product);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.productDetailModal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}>×</button>

        <div className={styles.productDetailContent}>

          {/* LEFT — Images */}
          <ProductImagesSection
            product={product}
            productImages={productImages}
            activeImageIndex={modal.activeImageIndex}
            setActiveImageIndex={modal.setActiveImageIndex}
            isLiked={modal.isLiked}
            isLiking={modal.isLiking}
            likes={modal.likes}
            onLikeToggle={modal.handleLikeToggle}
            relatedProducts={relatedProducts}
          />

          {/* MIDDLE — Product info & actions */}
          <ProductInfoSection
            product={product}
            showSellerInfo={modal.showSellerInfo}
            showCopyNotification={modal.showCopyNotification}
            checkAuthentication={modal.checkAuthentication}
            onContactSeller={modal.handleContactSellerClick}
            onMakeOffer={modal.handleMakeOffer}
            onMessageSeller={modal.handleMessageSeller}
            onOpenRating={modal.handleOpenRating}
            onSaveToWishlist={modal.handleSaveToWishlist}
            onCopyLink={modal.handleCopyLink}
          />

          {/* RIGHT — Seller sidebar */}
          <SellerSidebar
            product={product}
            sellerAvatarUrl={modal.sellerAvatarUrl}
            loggedInUser={modal.loggedInUser}
            isFollowing={modal.isFollowing}
            followCount={modal.followCount}
            sellerOtherProducts={sellerOtherProducts}
            onFollowToggle={modal.handleFollowToggle}
            onRelatedProductClick={modal.handleRelatedProductClick}
            onReportSeller={modal.handleOpenReport}
            isFollowLoading={modal.isFollowLoading}
          />
        </div>

        {/* BOTTOM — Similar items */}
        <SimilarItems
          relatedProducts={relatedProducts}
          onProductClick={modal.handleRelatedProductClick}
        />

        {/* SUB-MODALS */}
        {modal.showOfferModal && (
          <MakeOfferModal
            product={product}
            offerAmount={modal.offerAmount}
            setOfferAmount={modal.setOfferAmount}
            offerMessage={modal.offerMessage}
            setOfferMessage={modal.setOfferMessage}
            offerError={modal.offerError}
            isSubmittingOffer={modal.isSubmittingOffer}
            onSubmit={modal.handleSubmitOffer}
            onClose={() => modal.setShowOfferModal(false)}
          />
        )}

        {modal.showMessageModal && (
          <MessageSellerModal
            product={product}
            messageText={modal.messageText}
            setMessageText={modal.setMessageText}
            messageError={modal.messageError}
            setMessageError={modal.setMessageError}
            isSubmittingMessage={modal.isSubmittingMessage}
            onSubmit={modal.handleSubmitMessage}
            onClose={() => modal.setShowMessageModal(false)}
          />
        )}

        {modal.showRatingModal && (
          <RateSellerModal
            product={product}
            userRating={modal.userRating}
            setUserRating={modal.setUserRating}
            hoverRating={modal.hoverRating}
            setHoverRating={modal.setHoverRating}
            ratingComment={modal.ratingComment}
            setRatingComment={modal.setRatingComment}
            ratingSuccess={modal.ratingSuccess}
            ratingError={modal.ratingError}
            setRatingError={modal.setRatingError}
            isSubmittingRating={modal.isSubmittingRating}
            onSubmit={modal.handleSubmitRating}
            onClose={() => modal.setShowRatingModal(false)}
          />
        )}

        {modal.showReportModal && (
          <ReportSellerModal
            product={product}
            reportReason={modal.reportReason}
            setReportReason={modal.setReportReason}
            reportDetails={modal.reportDetails}
            setReportDetails={modal.setReportDetails}
            reportSuccess={modal.reportSuccess}
            reportError={modal.reportError}
            setReportError={modal.setReportError}
            isSubmittingReport={modal.isSubmittingReport}
            onSubmit={modal.handleSubmitReport}
            onClose={() => modal.setShowReportModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default ProductDetailModal;











